import React from "react";
import "../../styles/shared-ui.css";
import "../../styles/modal.css";
import "../../styles/task-detail-modal.css";
import { getJobsByCompanyId } from "../../services/jobService";
import { createTask } from "../../services/taskService";
import { getTaskCategories } from "../../services/taskCategoryService";
import { getTaskStatuses } from "../../services/taskStatusService";
import SideTabs from "../layout/SideTabs";
import RichTextEditor from "./RichTextEditor";
import SkillTable from "./SkillTable";
import SkillAddModal from "./SkillAddModal";
import RaciMatrix from "./RaciMatrix";
import { LEVELS } from "./levels";
import { toast } from "../../utils/toast";

// Task Management's Create Task form, packaged as a modal so a new task can be
// authored without leaving the diagram. Mirrors TaskCreatePage's fields and
// validation, including its rule that RACI is only assignable once the task
// exists.

const FREQUENCY_PERIODS = ["DAY", "WEEK", "MONTH", "YEAR"];

const describeRecurrence = (occurrences, frequencyInterval, frequencyPeriod) => {
  const occ = Number(occurrences);
  const interval = Number(frequencyInterval);
  if (
    !Number.isFinite(occ) ||
    !Number.isFinite(interval) ||
    occ <= 0 ||
    interval <= 0
  ) {
    return "";
  }
  const periodLabel = String(frequencyPeriod || "WEEK").toLowerCase();
  const periodText = interval === 1 ? periodLabel : `${periodLabel}s`;
  const timesText = occ === 1 ? "time" : "times";
  return `${occ} ${timesText} in ${interval} ${periodText}`;
};

const emptyForm = () => ({
  task_name: "",
  task_code: "",
  expected_process_time: "",
  optimistic_process_time: "",
  pessimistic_process_time: "",
  expected_waiting_time: "",
  expected_rework_time: "",
  status_id: "",
  category_id: "",
  task_overview: "",
  frequency_period: "WEEK",
  frequency_interval: "1",
  occurrences: "1",
  skills: [],
});

const validateForm = (f) => {
  const v = {};
  if (!String(f.task_name).trim()) v.task_name = "Task name is required";
  if (!String(f.task_code).trim()) v.task_code = "Task code is required";
  else if (!/^[A-Za-z0-9\-_.]+$/.test(String(f.task_code)))
    v.task_code =
      "Only letters, numbers, dash (-), underscore (_) and dot (.) are allowed";
  const overviewText = String(f.task_overview || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  if (overviewText.length < 10)
    v.task_overview = "Description must be at least 10 characters";
  const processTime = Number(f.expected_process_time);
  if (f.expected_process_time === "" || !Number.isFinite(processTime))
    v.expected_process_time = "Expected process time is required";
  else if (processTime < 0)
    v.expected_process_time = "Expected process time cannot be negative";
  return v;
};

/**
 * props:
 * - open
 * - companyId, companyName — the builder's company; the task is created for it
 *   and the field is fixed, since the workflow being edited belongs to it.
 * - onClose()
 * - onCreated(task) — task carries at least { task_id, task_name, task_code }
 */
const TaskCreateModal = ({
  open,
  companyId,
  companyName,
  onClose,
  onCreated,
}) => {
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [taskCategories, setTaskCategories] = React.useState([]);
  const [taskStatuses, setTaskStatuses] = React.useState([]);
  const [allJobs, setAllJobs] = React.useState([]);
  const [showAddSkill, setShowAddSkill] = React.useState(false);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const errors = React.useMemo(() => validateForm(form), [form]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setForm(emptyForm());
    setLoading(true);
    (async () => {
      const [cats, statuses, jobs] = await Promise.all([
        getTaskCategories().catch(() => []),
        getTaskStatuses().catch(() => []),
        companyId ? getJobsByCompanyId(companyId).catch(() => []) : [],
      ]);
      if (cancelled) return;
      setTaskCategories(Array.isArray(cats) ? cats : []);
      setTaskStatuses(Array.isArray(statuses) ? statuses : []);
      setAllJobs(Array.isArray(jobs) ? jobs : []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !showAddSkill) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, showAddSkill]);

  const recurrenceSummary = describeRecurrence(
    form.occurrences,
    form.frequency_interval,
    form.frequency_period,
  );

  const canSave =
    !loading && !!companyId && Object.keys(errors).length === 0;

  const onSubmit = async () => {
    if (!canSave || saving) return;
    try {
      setSaving(true);
      const created = await createTask({
        task_name: form.task_name,
        task_code: form.task_code,
        version: 0,
        task_company_id: Number(companyId),
        expected_process_time:
          form.expected_process_time === ""
            ? null
            : Number(form.expected_process_time),
        expected_waiting_time:
          form.expected_waiting_time === ""
            ? null
            : Number(form.expected_waiting_time),
        expected_rework_time:
          form.expected_rework_time === ""
            ? null
            : Number(form.expected_rework_time),
        status_id: form.status_id === "" ? null : Number(form.status_id),
        category_id: form.category_id === "" ? null : Number(form.category_id),
        task_overview: form.task_overview,
        frequency_period: form.frequency_period || "WEEK",
        frequency_interval:
          form.frequency_interval === "" ? 1 : Number(form.frequency_interval),
        occurrences: form.occurrences === "" ? 1 : Number(form.occurrences),
        taskSkills: (form.skills || [])
          .filter((s) => String(s.name).trim() && String(s.level))
          .map((s) => ({ skill_name: s.name.trim(), level: s.level })),
      });

      toast.success(`Task "${form.task_name}" has been created successfully!`);
      // The POST body shape varies by backend, so hand back what we know for
      // certain (the code we just sent) and let the caller resolve the id.
      onCreated?.({
        task_id:
          created?.task_id ?? created?.id ?? created?.data?.task_id ?? null,
        task_name: form.task_name,
        task_code: form.task_code,
        raw: created,
      });
    } catch (e) {
      toast.error(e?.message || "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const firstError =
    errors.task_name ||
    errors.task_code ||
    errors.expected_process_time ||
    errors.task_overview ||
    "";

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-panel tdm-panel">
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Create Task</h3>
            <div className="tdm-subtitle">
              Added to {companyName || "this company"} and dropped straight onto
              the workflow
            </div>
          </div>
          <button className="modal-btn" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>

        <div className="modal-body tdm-body">
          {loading ? (
            <div className="no-results">Loading...</div>
          ) : (
            <SideTabs
              defaultActiveId="basic"
              tabs={[
                {
                  id: "basic",
                  label: "Basic Details",
                  content: (
                    <div className="role-card">
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Task Name *</label>
                          <input
                            value={form.task_name}
                            onChange={(e) =>
                              setField("task_name", e.target.value)
                            }
                            placeholder="e.g. Assemble Machine"
                            autoFocus
                          />
                          {errors.task_name && (
                            <div className="tdm-error">{errors.task_name}</div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Task Code *</label>
                          <input
                            value={form.task_code}
                            onChange={(e) =>
                              setField("task_code", e.target.value)
                            }
                            placeholder="e.g. AS-MCH-002"
                          />
                          {errors.task_code && (
                            <div className="tdm-error">{errors.task_code}</div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Company</label>
                          <input value={companyName || "-"} disabled />
                          <small className="hint">
                            Fixed to the company whose process you're building.
                          </small>
                        </div>
                        <div className="form-group">
                          <label>Expected Process Time (min) *</label>
                          <input
                            type="number"
                            min={0}
                            value={form.expected_process_time}
                            onChange={(e) =>
                              setField("expected_process_time", e.target.value)
                            }
                            placeholder="e.g. 45"
                          />
                          {errors.expected_process_time && (
                            <div className="tdm-error">
                              {errors.expected_process_time}
                            </div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Optimistic Process Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.optimistic_process_time}
                            onChange={(e) =>
                              setField(
                                "optimistic_process_time",
                                e.target.value,
                              )
                            }
                            placeholder="best case — optional"
                          />
                        </div>
                        <div className="form-group">
                          <label>Pessimistic Process Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.pessimistic_process_time}
                            onChange={(e) =>
                              setField(
                                "pessimistic_process_time",
                                e.target.value,
                              )
                            }
                            placeholder="worst case — optional"
                          />
                        </div>
                        <div className="form-group">
                          <label>Expected Waiting Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.expected_waiting_time}
                            onChange={(e) =>
                              setField("expected_waiting_time", e.target.value)
                            }
                            placeholder="e.g. 10"
                          />
                        </div>
                        <div className="form-group">
                          <label>Expected Rework Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.expected_rework_time}
                            onChange={(e) =>
                              setField("expected_rework_time", e.target.value)
                            }
                            placeholder="e.g. 5"
                          />
                        </div>
                        <div className="form-group">
                          <label>Task Status</label>
                          <select
                            value={form.status_id}
                            onChange={(e) =>
                              setField("status_id", e.target.value)
                            }
                          >
                            <option value="">Select Status</option>
                            {taskStatuses.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.description || `Status ${s.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Task Category</label>
                          <select
                            value={form.category_id}
                            onChange={(e) =>
                              setField("category_id", e.target.value)
                            }
                          >
                            <option value="">Select task category</option>
                            {taskCategories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.description || c.name || `Category ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label title="Total number of times this task should occur within the recurrence window">
                            Occurrences
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={form.occurrences}
                            onChange={(e) =>
                              setField("occurrences", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label title="How many periods to wait between occurrences">
                            Repeat Interval
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={form.frequency_interval}
                            onChange={(e) =>
                              setField("frequency_interval", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label title="Unit of time used for the repeat interval">
                            Period
                          </label>
                          <select
                            value={form.frequency_period}
                            onChange={(e) =>
                              setField("frequency_period", e.target.value)
                            }
                          >
                            {FREQUENCY_PERIODS.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group tdm-full">
                          <label>Recurrence Summary</label>
                          <input value={recurrenceSummary} disabled />
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  id: "description",
                  label: "Description",
                  content: (
                    <div className="role-card">
                      <RichTextEditor
                        label="Task Description *"
                        value={form.task_overview}
                        onChange={(val) => setField("task_overview", val)}
                        height={240}
                      />
                      {errors.task_overview && (
                        <div className="tdm-error">{errors.task_overview}</div>
                      )}
                    </div>
                  ),
                },
                {
                  id: "skills",
                  label: "Req Skills",
                  content: (
                    <div className="role-card">
                      <div className="tdm-section-head">
                        <span>Req Skills</span>
                        <button
                          type="button"
                          className="secondary-btn sm"
                          onClick={() => setShowAddSkill(true)}
                        >
                          + Add Skill
                        </button>
                      </div>
                      <SkillTable
                        mode="manage"
                        skills={form.skills}
                        levels={LEVELS}
                        onDelete={(idx) =>
                          setForm((prev) => ({
                            ...prev,
                            skills: prev.skills.filter((_, i) => i !== idx),
                          }))
                        }
                      />
                      <SkillAddModal
                        open={showAddSkill}
                        onClose={() => setShowAddSkill(false)}
                        levels={LEVELS}
                        onSave={(s) => {
                          setForm((prev) => ({
                            ...prev,
                            skills: [...prev.skills, s],
                          }));
                          setShowAddSkill(false);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  id: "raci",
                  label: "RACI",
                  content: (
                    <RaciMatrix
                      mode="manage"
                      jobs={allJobs}
                      rows={[]}
                      onChange={() => {}}
                      label={form.task_name}
                      disabled
                      disabledMessage="Create the task first — its RACI opens automatically right after, and the workflow can't be saved until a job is assigned."
                    />
                  ),
                },
              ]}
            />
          )}
        </div>

        <div className="modal-footer">
          {firstError && !loading && (
            <span className="tdm-footer-hint">{firstError}</span>
          )}
          <button className="modal-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="primary-btn"
            onClick={onSubmit}
            disabled={!canSave || saving}
          >
            {saving ? "Creating..." : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskCreateModal;
